// 견적 계산기 + 3단계 상담 신청 (contact.php 전용)
// 가격 데이터는 PHP가 내려준 window.__ESTIMATE_CONFIG__ 에서 조회한다.

const SUBMIT_URL = "/contact/estimate";

export function initContactEstimate() {
  const root = document.querySelector("[data-estimate]");
  if (!root) return;

  const cfg = window.__ESTIMATE_CONFIG__ || { grades: {}, categories: [] };
  const grades = cfg.grades || {};
  let inquiryStarted = false;

  function trackAnalytics(name, details) {
    if (typeof window.NL_TRACK === "function") {
      window.NL_TRACK(name, details || {});
    }
  }

  // option_id -> { label, unit, is_custom, prices, categoryId, categoryName, unitLabel }
  const optionMap = {};
  (cfg.categories || []).forEach((cat) => {
    (cat.options || []).forEach((opt) => {
      optionMap[String(opt.id)] = {
        label: opt.label,
        unit: opt.unit,
        isCustom: !!opt.is_custom,
        prices: opt.prices || {},
        categoryId: cat.id,
        categoryName: cat.name,
        unitLabel: cat.unit_label,
      };
    });
  });

  /** @type {Array<object>} 담긴 견적 항목 */
  let selectedItems = [];
  let currentStep = 1;

  const fmt = (n) => Number(n || 0).toLocaleString("ko-KR") + "원";

  // ── 모바일 토스트 (견적 담기 알림) ──────────────────
  // 모바일에서는 요약 카드가 화면 아래쪽에 위치해 담김 여부를 바로 확인하기 어렵다.
  // 견적을 담을 때마다 하단에 짧게 확인 토스트를 띄운다.
  const mobileMq = window.matchMedia("(max-width: 768px)");
  let toastEl = null;
  let toastTimer = null;

  function showAddedToast(item) {
    if (!mobileMq.matches) return;
    if (!toastEl) {
      toastEl = document.createElement("div");
      toastEl.className = "no-estimate-toast";
      toastEl.setAttribute("role", "status");
      toastEl.setAttribute("aria-live", "polite");
      document.body.appendChild(toastEl);
    }
    const total = selectedItems.reduce((s, it) => s + it.subtotal, 0);
    toastEl.innerHTML = `
      <i class="fa-solid fa-circle-check no-estimate-toast__icon"></i>
      <span class="no-estimate-toast__text">
        <strong>${esc(item.categoryName)}</strong> 견적이 담겼습니다
        <em>총 ${fmt(total)}</em>
      </span>`;
    // 재트리거 시 애니메이션 리셋
    toastEl.classList.remove("is-show");
    void toastEl.offsetWidth;
    toastEl.classList.add("is-show");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove("is-show"), 2200);
  }

  // ── 새로고침 대비 상태 저장 (sessionStorage: 탭 닫으면 자동 삭제) ──
  const STORE_KEY = "hb_estimate_state_v1";
  const STORE_TTL_MS = 30 * 60 * 1000; // 30분

  function saveState() {
    if (currentStep === 3) return; // 신청 완료 후에는 저장하지 않음
    try {
      const payload = {
        savedAt: Date.now(),
        step: currentStep,
        items: selectedItems.map((it) => ({
          optionId: it.optionId,
          grade: it.grade,
          quantity: it.quantity,
          customValue: it.customValue,
        })),
        form: form
          ? {
              company_name: form.company_name.value,
              phone: form.phone.value,
              email: form.email.value,
              budget: form.budget.value,
              contents: form.contents.value,
              privacy_agree: form.privacy_agree.checked,
            }
          : null,
      };
      sessionStorage.setItem(STORE_KEY, JSON.stringify(payload));
    } catch (_) {
      /* storage 사용 불가 시 무시 */
    }
  }

  function clearState() {
    try {
      sessionStorage.removeItem(STORE_KEY);
    } catch (_) {
      /* noop */
    }
  }

  function loadState() {
    try {
      const raw = sessionStorage.getItem(STORE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const savedAt = Number(parsed?.savedAt || 0);
      if (!savedAt || Date.now() - savedAt > STORE_TTL_MS) {
        sessionStorage.removeItem(STORE_KEY);
        return null;
      }
      return parsed;
    } catch (_) {
      return null;
    }
  }

  /** 저장된 항목을 현재 설정(optionMap)으로 다시 검증·계산해 복원 */
  function restoreItems(savedItems) {
    const out = [];
    (savedItems || []).forEach((s) => {
      const meta = optionMap[String(s.optionId)];
      if (!meta) return; // 삭제된 옵션은 건너뜀
      const grade = s.grade;
      if (!(grade in meta.prices)) return; // 사라진 등급은 건너뜀
      const unitPrice = Number(meta.prices[grade] || 0);
      const quantity = Math.max(1, parseInt(s.quantity, 10) || 1);
      const customValue = s.customValue || "";
      const customUnits = meta.isCustom
        ? Math.max(1, parseInt(customValue, 10) || 1)
        : 1;
      out.push({
        key: `${s.optionId}-${grade}`,
        categoryId: meta.categoryId,
        categoryName: meta.categoryName,
        optionId: Number(s.optionId),
        optionLabel: meta.label,
        unit: meta.unit,
        unitLabel: meta.unitLabel,
        isCustom: meta.isCustom,
        customValue,
        grade,
        gradeLabel: grades[grade] || grade,
        quantity,
        unitPrice,
        subtotal: unitPrice * customUnits * quantity,
      });
    });
    return out;
  }

  // ── 카드 인터랙션 ───────────────────────────────────
  root.querySelectorAll(".no-estimate-card").forEach((card) => {
    const customInput = card.querySelector("[data-custom-input]");

    // 평수/면수 옵션
    card.querySelectorAll("[data-option]").forEach((btn) => {
      btn.addEventListener("click", () => {
        setActive(card.querySelectorAll("[data-option]"), btn);
        const isCustom = btn.dataset.isCustom === "1";
        if (customInput) {
          customInput.hidden = !isCustom;
          if (!isCustom) customInput.value = "";
          if (isCustom) customInput.focus();
        }
      });
    });

    // 등급
    card.querySelectorAll("[data-grade]").forEach((btn) => {
      btn.addEventListener("click", () => {
        setActive(card.querySelectorAll("[data-grade]"), btn);
      });
    });

    // 수량 +/-
    const qtyInput = card.querySelector("[data-qty-input]");
    card.querySelector("[data-qty-minus]")?.addEventListener("click", () => {
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
    });
    card.querySelector("[data-qty-plus]")?.addEventListener("click", () => {
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + 1);
    });
    qtyInput?.addEventListener("change", () => {
      const v = parseInt(qtyInput.value, 10);
      qtyInput.value = !v || v < 1 ? 1 : v;
    });

    // 견적 담기
    card.querySelector("[data-add]")?.addEventListener("click", () => {
      addCurrent(card);
    });
  });

  function setActive(nodeList, target) {
    nodeList.forEach((n) => n.classList.toggle("is-active", n === target));
  }

  function addCurrent(card) {
    const optBtn = card.querySelector("[data-option].is-active");
    if (!optBtn) {
      window.alert("평수/면수를 선택해주세요.");
      return;
    }
    const gradeBtn = card.querySelector("[data-grade].is-active");
    if (!gradeBtn) {
      window.alert("등급을 선택해주세요.");
      return;
    }

    const optionId = optBtn.dataset.optionId;
    const meta = optionMap[String(optionId)];
    if (!meta) return;

    const grade = gradeBtn.dataset.grade;
    const unitPrice = Number(meta.prices[grade] || 0);
    const quantity = Math.max(
      1,
      parseInt(card.querySelector("[data-qty-input]").value, 10) || 1
    );

    let customValue = "";
    // 직접입력 옵션은 입력한 평수(customValue)가 단가에 곱해지는 배수가 된다.
    // 일반 옵션은 저장 단가가 이미 절대가격이므로 배수 1.
    let customUnits = 1;
    if (meta.isCustom) {
      const raw = (card.querySelector("[data-custom-input]")?.value || "").replace(
        /\D+/g,
        ""
      );
      if (!raw) {
        window.alert("직접 입력 평수를 숫자로 입력해주세요.");
        return;
      }
      customValue = raw;
      customUnits = Math.max(1, parseInt(raw, 10) || 1);
    }

    const key = `${optionId}-${grade}`;
    const item = {
      key,
      categoryId: meta.categoryId,
      categoryName: meta.categoryName,
      optionId: Number(optionId),
      optionLabel: meta.label,
      unit: meta.unit,
      unitLabel: meta.unitLabel,
      isCustom: meta.isCustom,
      customValue,
      grade,
      gradeLabel: grades[grade] || grade,
      quantity,
      unitPrice,
      subtotal: unitPrice * customUnits * quantity,
    };

    // 동일 옵션+등급은 갱신, 그 외는 추가
    const idx = selectedItems.findIndex((it) => it.key === key);
    if (idx >= 0) selectedItems[idx] = item;
    else selectedItems.push(item);

    renderSummary();
    showAddedToast(item);
  }

  // ── 요약 렌더링 (Step1·Step2 공통) ──────────────────
  function renderSummary() {
    const total = selectedItems.reduce((s, it) => s + it.subtotal, 0);

    root.querySelectorAll("[data-estimate-summary]").forEach((box) => {
      const list = box.querySelector("[data-summary-list]");
      const empty = box.querySelector("[data-summary-empty]");
      const totalEl = box.querySelector("[data-summary-total]");

      if (list) {
        list.innerHTML = selectedItems.map(renderItem).join("");
      }
      if (empty) empty.hidden = selectedItems.length > 0;
      if (totalEl) {
        totalEl.innerHTML =
          Number(total || 0).toLocaleString("ko-KR") +
          '<span class="no-estimate-summary__total-unit">원</span>';
      }
    });

    // 항목 없으면 신청 단계로 못 넘어가게
    root.querySelectorAll("[data-go-step2]").forEach((btn) => {
      btn.disabled = selectedItems.length === 0;
    });

    // 삭제 버튼 바인딩
    root.querySelectorAll("[data-remove]").forEach((btn) => {
      btn.addEventListener("click", () => {
        selectedItems = selectedItems.filter((it) => it.key !== btn.dataset.remove);
        renderSummary();
      });
    });

    saveState();
  }

  function renderItem(it) {
    const optionDisplay = it.isCustom
      ? `${it.customValue}${it.unit}`
      : it.optionLabel;
    const meta = `${optionDisplay} · ${it.unitLabel} ${it.quantity} · ${it.gradeLabel}`;
    return `
      <li class="no-estimate-summary__item">
        <span class="no-estimate-summary__item-name">${esc(it.categoryName)}</span>
        <span class="no-estimate-summary__item-price">${fmt(it.subtotal)}</span>
        <span class="no-estimate-summary__item-meta">${esc(meta)}</span>
        <button type="button" class="no-estimate-summary__item-remove" data-remove="${it.key}" aria-label="삭제">
          <i class="fa-light fa-xmark"></i>
        </button>
      </li>`;
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    }[c]));
  }

  // ── 단계 전환 ───────────────────────────────────────
  function showStep(n, scroll = true) {
    currentStep = n;
    if (n === 2 && !inquiryStarted) {
      inquiryStarted = true;
      trackAnalytics("inquiry_start", {
        category: "estimate",
        label: "contact_estimate",
      });
    }
    root.querySelectorAll("[data-step]").forEach((step) => {
      const active = Number(step.dataset.step) === n;
      step.hidden = !active;
      step.classList.toggle("is-active", active);
    });
    root.querySelectorAll("[data-step-indicator]").forEach((li) => {
      li.classList.toggle("is-active", Number(li.dataset.stepIndicator) === n);
    });
    if (n === 3) clearState();
    else saveState();
    if (scroll) {
      const top = root.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }
  }

  root.querySelectorAll("[data-go-step2]").forEach((btn) =>
    btn.addEventListener("click", () => {
      if (selectedItems.length === 0) {
        window.alert("견적 항목을 1개 이상 담아주세요.");
        return;
      }
      showStep(2);
    })
  );
  root.querySelectorAll("[data-go-step1]").forEach((btn) =>
    btn.addEventListener("click", () => showStep(1))
  );

  // ── 개인정보 동의 모달 ──────────────────────────────
  const modal = document.querySelector("[data-privacy-modal]");
  document.querySelectorAll("[data-privacy-open]").forEach((btn) =>
    btn.addEventListener("click", () => {
      if (modal) modal.hidden = false;
    })
  );
  document.querySelectorAll("[data-privacy-close]").forEach((btn) =>
    btn.addEventListener("click", () => {
      if (modal) modal.hidden = true;
    })
  );

  // ── 폼 제출 ─────────────────────────────────────────
  const form = root.querySelector("[data-estimate-form]");
  if (form) {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      clearErrors(form);

      const err = validateForm(form);
      if (err) {
        if (err.field) {
          const el = form.querySelector(`[name="${err.field}"]`);
          el?.classList.add("is-error");
          el?.focus();
        }
        window.alert(err.message);
        return;
      }
      if (selectedItems.length === 0) {
        window.alert("견적 항목을 1개 이상 담아주세요.");
        showStep(1);
        return;
      }

      const submitBtn = form.querySelector("[data-submit]");
      if (submitBtn) submitBtn.disabled = true;

      const body = new FormData();
      body.append("company_name", form.company_name.value.trim());
      body.append("phone", form.phone.value.trim());
      body.append("email", form.email.value.trim());
      body.append("budget", form.budget.value.trim());
      body.append("contents", form.contents.value.trim());
      body.append("privacy_agree", form.privacy_agree.checked ? "1" : "");
      body.append(
        "items",
        JSON.stringify(
          selectedItems.map((it) => ({
            option_id: it.optionId,
            grade: it.grade,
            quantity: it.quantity,
            custom_value: it.customValue,
          }))
        )
      );

      try {
        const res = await fetch(SUBMIT_URL, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body,
        });
        const data = await res.json();

        if (data && data.result === "success") {
          trackAnalytics("inquiry_submit", {
            category: "estimate",
            label: "contact_estimate",
          });
          clearState();
          showStep(3);
        } else {
          window.alert((data && data.message) || "신청 처리에 실패했습니다.");
          applyFieldErrors(form, (data && data.errors) || {});
        }
      } catch (_) {
        window.alert("네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    });
  }

  function validateForm(f) {
    if (!f.company_name.value.trim())
      return { field: "company_name", message: "상호명(이름)을 입력해주세요." };

    const phoneDigits = f.phone.value.replace(/\D+/g, "");
    if (phoneDigits.length < 9)
      return { field: "phone", message: "연락처를 정확히 입력해주세요." };

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.value.trim()))
      return { field: "email", message: "이메일을 정확히 입력해주세요." };

    if (!f.budget.value.trim())
      return { field: "budget", message: "사업 예산을 입력해주세요." };

    if (!f.contents.value.trim())
      return { field: "contents", message: "문의 내용을 입력해주세요." };

    if (!f.privacy_agree.checked)
      return { field: "privacy_agree", message: "개인정보 수집 및 이용에 동의해주세요." };

    return null;
  }

  function clearErrors(f) {
    f.querySelectorAll(".is-error").forEach((el) => el.classList.remove("is-error"));
  }

  function applyFieldErrors(f, errors) {
    Object.keys(errors || {}).forEach((name) => {
      f.querySelector(`[name="${name}"]`)?.classList.add("is-error");
    });
  }

  // ── 전체 초기화 ─────────────────────────────────────
  root.querySelector("[data-restart]")?.addEventListener("click", () => {
    selectedItems = [];
    root.querySelectorAll(".is-active").forEach((el) => {
      if (el.matches("[data-option], [data-grade]")) el.classList.remove("is-active");
    });
    root.querySelectorAll("[data-custom-input]").forEach((inp) => {
      inp.hidden = true;
      inp.value = "";
    });
    root.querySelectorAll("[data-qty-input]").forEach((inp) => (inp.value = 1));
    if (form) {
      form.reset();
      clearErrors(form);
    }
    clearState();
    inquiryStarted = false;
    renderSummary();
    showStep(1);
  });

  // ── 폼 입력값 자동 저장 (새로고침 대비) ──────────────
  if (form) {
    form.addEventListener("input", saveState);
    form.addEventListener("change", saveState);
  }

  // ── 새로고침 시 이전 상태 복원 ──────────────────────
  const saved = loadState();
  if (saved) {
    selectedItems = restoreItems(saved.items);
    if (saved.form && form) {
      form.company_name.value = saved.form.company_name || "";
      form.phone.value = saved.form.phone || "";
      form.email.value = saved.form.email || "";
      form.budget.value = saved.form.budget || "";
      form.contents.value = saved.form.contents || "";
      form.privacy_agree.checked = !!saved.form.privacy_agree;
    }
  }

  renderSummary();

  // 단계 복원: 담긴 항목이 없는데 2단계면 1단계로 보정
  let initialStep = saved && saved.step ? Number(saved.step) : 1;
  if (initialStep === 2 && selectedItems.length === 0) initialStep = 1;
  if (initialStep !== 1) showStep(initialStep, false);
}
